/**
 * @file BannersPage.tsx
 * @brief Super Admin → Home Ads — manage mobile home-screen slider banners.
 *
 * @details
 * Flush GridBlock layout: summary KPIs, catalogue band, and banner rows
 * as divide-y list cells (no card chrome). Create / edit via dialog.
 *
 * @dependencies
 * - features/banners/{api,schemas,components}
 *
 * @references
 * - FRONTEND/src/screens/SuperAdmin/tabs/BannersTab.tsx
 * - BACKEND/src/features/banners/routes.js
 *
 * @author DES
 * @date 07-06-2026
 * @updated 29-07-2026 — end-to-end GridCell list rows
 */

import { DockActionButton, PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import { EmptyState } from "@/components/feedback/EmptyState";
import { useMemo, useState } from "react";
import { Image as ImageIcon, LoaderCircle, Plus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { useBanners } from "./api";
import { BannerCard } from "./components/BannerCard";
import { BannerFormDialog } from "./components/BannerFormDialog";
import type { Banner } from "./schemas";

export function BannersPage() {
  const { data, isLoading, isError, refetch, isFetching } = useBanners();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<Banner | null>(null);

  const banners = useMemo(() => data ?? [], [data]);

  const totals = useMemo(() => {
    let active = 0;
    let disabled = 0;
    for (const banner of banners) {
      if (banner.is_enabled) active++;
      else disabled++;
    }
    return { total: banners.length, active, disabled };
  }, [banners]);

  const openCreate = () => {
    setSelected(null);
    setDialogOpen(true);
  };

  const openEdit = (banner: Banner) => {
    setSelected(banner);
    setDialogOpen(true);
  };

  return (
    <div className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Home Ads</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Manage the promotional slider on the mobile home screen. Lower
              display order values appear first.
            </p>
          </div>
        </GridBand>

        <GridCells className="grid-cols-1 sm:grid-cols-3">
          <SummaryTile label="Total" value={totals.total} />
          <SummaryTile label="Active" value={totals.active} tone="success" />
          <SummaryTile label="Disabled" value={totals.disabled} tone="muted" />
        </GridCells>

        <GridBand className="flex items-center gap-2 text-sm font-medium">
          <ImageIcon className="h-4 w-4 text-primary" />
          Banners
          <span className="font-normal text-muted-foreground">
            ·{" "}
            {isFetching && !isLoading
              ? "Refreshing…"
              : `${banners.length} banner(s) configured.`}
          </span>
        </GridBand>

        <GridCell padded={false}>
          {isLoading ? (
            <div className="space-y-3 p-4 md:p-5">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-28 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="px-4 py-8 text-center md:px-5">
              <p className="text-sm text-destructive">
                Couldn&apos;t load banners.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => refetch()}
              >
                Retry
              </Button>
            </div>
          ) : banners.length === 0 ? (
            <EmptyState
              shape="row"
              title="No banners yet"
              description="Create your first promotional slide for the mobile home screen."
              action={
                <button type="button" onClick={openCreate} className="inline-flex h-12 items-center gap-2 rounded-full bg-brand px-6 text-[15px] font-semibold text-white transition-colors hover:bg-brand/90">
                  <Plus className="h-5 w-5" />
                  Add banner
                </button>
              }
            />
          ) : (
            <div className="divide-y divide-border/60">
              {banners.map((banner) => (
                <BannerCard key={banner.id} banner={banner} onEdit={openEdit} />
              ))}
            </div>
          )}

          {isFetching && !isLoading ? (
            <div className="flex items-center justify-center gap-2 border-t border-border/60 py-2 text-xs text-muted-foreground">
              <LoaderCircle className="h-3 w-3 animate-spin" />
              Syncing…
            </div>
          ) : null}
        </GridCell>
      </GridBlock>

      {/* The page's action, docked at the bottom (DES 01-10-2026). */}
      <PageDock>
        <DockActionButton icon={<Plus className="h-5 w-5" strokeWidth={2} />} onClick={openCreate}>
          Add banner
        </DockActionButton>
      </PageDock>

      <BannerFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        banner={selected}
      />
    </div>
  );
}

interface SummaryTileProps {
  label: string;
  value: number;
  tone?: "success" | "muted" | "default";
}

function SummaryTile({ label, value, tone = "default" }: SummaryTileProps) {
  const toneClass =
    tone === "success"
      ? "text-success"
      : tone === "muted"
        ? "text-muted-foreground"
        : "text-foreground";

  return (
    <GridCell>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</p>
    </GridCell>
  );
}
